import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import { toast } from 'sonner';
import { useBusinessTimezone, formatDateOnlyDisplay } from '../../utils/businessTime';
import { 
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Activity,
  Filter
} from 'lucide-react';

const ParentAttendance = () => {
  const businessTz = useBusinessTimezone();
  const [children, setChildren] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [selectedChild]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [childrenRes] = await Promise.all([
        api.get('/players')
      ]);
      setChildren(childrenRes.data || []);
      
      // Fetch attendance for all children
      await fetchAttendance();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      let url = '/attendance';
      if (selectedChild && selectedChild !== 'all') {
        url += `?player_id=${selectedChild}`;
      }
      const attendanceRes = await api.get(url);
      setAttendance(attendanceRes.data || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setAttendance([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Present' },
      absent: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Absent' },
      late: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Late' },
      excused: { color: 'bg-blue-100 text-blue-700', icon: Calendar, label: 'Excused' }
    };
    const config = statusConfig[status] || statusConfig.present;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} capitalize flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Calculate attendance stats
  const calculateStats = () => {
    const filteredAttendance = selectedChild === 'all' 
      ? attendance 
      : attendance.filter(a => a.player_id === selectedChild);
    
    const total = filteredAttendance.length;
    const present = filteredAttendance.filter(a => a.status === 'present').length;
    const absent = filteredAttendance.filter(a => a.status === 'absent').length;
    const late = filteredAttendance.filter(a => a.status === 'late').length;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;

    return { total, present, absent, late, rate };
  };

  const stats = calculateStats();

  // Filter attendance by date
  const filteredAttendance = attendance.filter(record => {
    if (dateFilter) {
      return record.date?.startsWith(dateFilter);
    }
    return true;
  });

  // Group attendance by child for summary view
  const attendanceByChild = children.map(child => {
    const childAttendance = attendance.filter(a => a.player_id === child.id);
    const total = childAttendance.length;
    const present = childAttendance.filter(a => a.status === 'present').length;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;
    
    return {
      ...child,
      total,
      present,
      absent: childAttendance.filter(a => a.status === 'absent').length,
      rate
    };
  });

  if (loading) {
    return (
      <DashboardLayout title="Attendance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Attendance">
      <div className="space-y-6" data-testid="parent-attendance-page">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Attendance</h1>
          <p className="text-slate-500">View your children's class attendance records</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Classes</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Present</p>
                  <p className="text-2xl font-bold text-green-600">{stats.present}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Absent</p>
                  <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Attendance Rate</p>
                  <p className={`text-2xl font-bold ${stats.rate >= 80 ? 'text-green-600' : stats.rate >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                    {stats.rate}%
                  </p>
                </div>
                <div className="p-3 bg-slate-100 rounded-full">
                  <Activity className="h-6 w-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Per-Child Summary */}
        {children.length > 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Attendance by Child
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {attendanceByChild.map((child) => (
                  <div 
                    key={child.id} 
                    className="p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => setSelectedChild(child.id)}
                    data-testid={`child-attendance-${child.id}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold">
                          {child.full_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{child.full_name}</p>
                        <p className="text-sm text-slate-500">{child.total} classes</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3 text-sm">
                        <span className="text-green-600">{child.present} present</span>
                        <span className="text-red-600">{child.absent} absent</span>
                      </div>
                      <Badge className={`${child.rate >= 80 ? 'bg-green-100 text-green-700' : child.rate >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {child.rate}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Filters:</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm">Child:</Label>
                <Select value={selectedChild} onValueChange={setSelectedChild}>
                  <SelectTrigger className="w-48" data-testid="child-filter">
                    <SelectValue placeholder="All Children" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Children</SelectItem>
                    {children.map((child) => (
                      <SelectItem key={child.id} value={child.id}>
                        {child.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm">Month:</Label>
                <Input
                  type="month"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-40"
                  data-testid="date-filter"
                />
              </div>
              
              {(selectedChild !== 'all' || dateFilter) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setSelectedChild('all');
                    setDateFilter('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Attendance Records Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Attendance History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredAttendance.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Child</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAttendance.map((record) => (
                    <TableRow key={record.id} data-testid={`attendance-row-${record.id}`}>
                      <TableCell className="font-medium">
                        {/^\d{4}-\d{2}-\d{2}$/.test(String(record.date || ''))
                          ? formatDateOnlyDisplay(record.date, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                          : new Date(record.date).toLocaleDateString('en-US', {
                              timeZone: businessTz,
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 text-sm font-bold">
                              {record.player_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          {record.player_name}
                        </div>
                      </TableCell>
                      <TableCell>{record.class_name || '-'}</TableCell>
                      <TableCell>{getStatusBadge(record.status)}</TableCell>
                      <TableCell className="text-slate-500 max-w-xs truncate">
                        {record.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">No attendance records found</p>
                <p className="text-sm">Attendance will appear here once classes are attended.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ParentAttendance;
